import { Article } from '@wix/answers-api';
import { pageClick } from '@wix/bi-logger-customer-care-data/v2';
import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import React, { FunctionComponent } from 'react';

import { BI } from '../../../../constants';
import { DATA_HOOKS } from '../../../../dataHooks';
import { useBI } from '../../../../hooks/useBI';
import {
  ItemOnPageEventSource,
  useBiOnVisibleItem,
} from '../../../../hooks/useBiOnVisibleItem';
import useIsHelpCenterCopilotEnabled from '../../../../hooks/useIsHelpCenterCopilotEnabled';
import { ArticleMediumIcon } from '../../../../icons/ArticleMediumIcon';
import { PageType } from '../../../../types';
import { mapPageTypeToSourceName } from '../../../../utils/bi';
import { useStore } from '../../../../zustand/stores';

import css from './index.module.scss';

export type RelatedContentProps = {
  relatedArticles: Article[];
  selfArticleRedirect: (
    url: string,
    shallow?: boolean,
    targetLocale?: string
  ) => void;
};

export const RelatedContent: FunctionComponent<RelatedContentProps> = ({
  relatedArticles,
  selfArticleRedirect,
}: RelatedContentProps) => {
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();
  const { locale } = useRouter();
  const itemId = useStore((state) => state.pageSlice.itemId);
  const itemType = useStore((state) => state.pageSlice.itemType);
  const { itemRef } = useBiOnVisibleItem(
    ItemOnPageEventSource.RELATED_CONTENT,
    { item_id: itemId }
  );
  const isHelpCenterCopilotEnabled = useIsHelpCenterCopilotEnabled();

  const relatedContentClick = async (
    text: string,
    article: Article,
    order: string
  ) => {
    await sendBIEvent(
      pageClick({
        source_name: mapPageTypeToSourceName(PageType.Article),
        kb_lang: locale as string,
        item_id: itemId,
        item_type: itemType,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.RELATED_CONTENT,
        clicked_url: `/${locale}${article.uri}`,
        clicked_text: text,
        clicked_item_order: order,
        clicked_item_id: article.id,
        is_page_content_click: false,
      })
    );
  };

  return relatedArticles.length > 0 ? (
    <Box
      direction="vertical"
      gap="24px"
      className={classNames(css.relatedContent, {
        [css.relatedContentCopilot]: isHelpCenterCopilotEnabled,
      })}
    >
      <Text size="medium" weight="bold">
        {t('related-content.title')}
      </Text>
      <Box ref={itemRef} gap="12px" className={css.relatedContentItems}>
        {relatedArticles.map((relatedArticle: Article, index: number) => (
          <div
            key={relatedArticle.id}
            onClick={async () => {
              await relatedContentClick(
                relatedArticle.title,
                relatedArticle,
                `${index + 1}`
              );
              selfArticleRedirect(
                `/${locale}${relatedArticle.uri}`,
                false,
                relatedArticle.locale
              );
            }}
            className={css.relatedArticle}
            data-hook={DATA_HOOKS.RELATED_ARTICLE_LINK}
            data-href={`/${locale}${relatedArticle.uri}`}
            style={{ cursor: 'pointer' }}
          >
            <Box className={css.relatedContentItem}>
              <ArticleMediumIcon />
              <Text dataHook={DATA_HOOKS.RELATED_ARTICLE_TEXT} ellipsis>
                {relatedArticle.title}
              </Text>
            </Box>
          </div>
        ))}
      </Box>
    </Box>
  ) : null;
};
